(() => {
  if (customElements.get('tipelle-video-carousel')) return;
  class TipelleVideoCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('.product-video-carousel__track');
      this.status = this.querySelector('[data-review-status]');
      this.active = null;
      this.mobile = matchMedia('(max-width:749px)');
      this.entries = Array.from(this.querySelectorAll('[data-review-toggle]'), (card, index) => ({
        card, index, video: card.querySelector('[data-review-video]'),
        requested: false, request: 0, frameCallback: null, revealFrame: null, previewPending: false, previewVisible: false, previewBlocked: false, mobilePrepared: false, poster: card.querySelector('[data-review-video]').getAttribute('poster') || card.querySelector('[data-review-video]').dataset.llnDesktopPoster, signal: this.controller.signal
      }));
      const previous = this.querySelector('[data-carousel-prev]');
      const next = this.querySelector('[data-carousel-next]');
      const update = () => {
        previous.disabled = this.track.scrollLeft <= 2;
        next.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
      };
      const move = direction => this.track.scrollBy({
        left: direction * (this.entries[0].card.parentElement.offsetWidth + parseFloat(getComputedStyle(this.track).gap)),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      this.track.addEventListener('scroll', update, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.entries.forEach(entry => {
        this.sync(entry);
        // Native button activation supplies click for touch, Enter and Space.
        entry.card.addEventListener('click', () => {
          if (this.mobile.matches) this.openFullscreen(entry);
          else if (entry.requested) this.stop(entry);
          else this.start(entry);
        }, options);
        entry.card.addEventListener('pointerenter', () => this.warm(entry), options);
        entry.card.addEventListener('pointerdown', () => this.warm(entry), options);
        entry.card.addEventListener('focus', () => this.warm(entry), options);
        entry.video.addEventListener('play', () => {
          if (this.mobile.matches) {
            entry.video.muted = true;
            entry.video.volume = 0;
            if (!this.wantsPreview(entry)) this.pausePreview(entry);
            return;
          }
          if (!this.isCurrent(entry) || document.hidden) this.stop(entry);
        }, options);
        entry.video.addEventListener('playing', () => {
          if (this.mobile.matches) {
            if (!this.wantsPreview(entry)) this.pausePreview(entry);
            this.sync(entry);
            return;
          }
          if (!this.isCurrent(entry) || document.hidden) { this.stop(entry); return; }
          this.revealWhenPainted(entry);
          this.sync(entry);
        }, options);
        entry.video.addEventListener('pause', () => {
          // Ignore an old queued pause event if a newer click has already resumed playback.
          if (entry.video.paused && entry.requested) this.stop(entry);
        }, options);
        entry.video.addEventListener('ended', () => {
          if (entry.video.ended) this.stop(entry);
        }, options);
        entry.video.addEventListener('canplay', () => { if (this.mobile.matches) this.preview(entry); }, options);
        entry.video.addEventListener('volumechange', () => {
          if (!this.mobile.matches) return;
          if (!entry.video.muted) entry.video.muted = true;
          if (entry.video.volume !== 0) entry.video.volume = 0;
        }, options);
        entry.video.addEventListener('error', () => {
          if (!entry.video.error) return;
          if (window.LlovableMedia.fallback(entry.video)) {
            if (this.mobile.matches) { entry.previewBlocked = false; this.preview(entry); }
            else if (this.isCurrent(entry)) this.play(entry, ++entry.request);
          } else this.failed(entry, entry.request);
        }, options);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAll();
        else if (this.mobile.matches) {
          this.entries.forEach(entry => { entry.previewBlocked = false; });
          this.measurePreviewVisibility();
        }
      }, options);
      document.addEventListener('tipelle:review-play', event => { if (event.detail !== this && !this.mobile.matches) this.pauseAll(); }, options);
      this.mobile.addEventListener('change', () => this.applyMode(), options);
      this.previewMeasureFrame = null;
      if ('IntersectionObserver' in window) {
        // Default viewport root also clips each card to the horizontal track.
        this.previewObserver = new IntersectionObserver(changes => {
          if (!this.mobile.matches) return;
          changes.forEach(change => {
            const entry = this.entries.find(item => item.card === change.target);
            if (!entry) return;
            this.setPreviewVisibility(entry, change.isIntersecting && change.intersectionRatio > 0);
          });
        }, {threshold: [0, 0.01]});
      } else {
        const schedule = () => this.schedulePreviewMeasure();
        this.track.addEventListener('scroll', schedule, {...options, passive:true});
        window.addEventListener('scroll', schedule, {...options, passive:true});
        window.addEventListener('resize', schedule, {...options, passive:true});
      }
      // Buffer desktop cards before the shopper reaches them, without autoplaying or
      // downloading every off-screen carousel video on initial page load.
      if (!this.mobile.matches && 'IntersectionObserver' in window) {
        this.preloadObserver = new IntersectionObserver(changes => {
          changes.forEach(change => {
            if (!change.isIntersecting) return;
            const entry = this.entries.find(item => item.card === change.target);
            if (entry) this.warm(entry);
            this.preloadObserver.unobserve(change.target);
          });
        }, { rootMargin: '300px 0px' });
        this.entries.forEach(entry => this.preloadObserver.observe(entry.card));
      } else if (!this.mobile.matches) {
        this.entries.slice(0, 3).forEach(entry => this.warm(entry));
      }
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(this.track);
      update();
      this.applyMode();
    }
    wantsPreview(entry) {
      return this.mobile.matches && entry.previewVisible && !document.hidden && !this.fullscreen && !entry.signal.aborted;
    }
    preparePreview(entry) {
      if (entry.mobilePrepared) return;
      entry.mobilePrepared = true;
      const video = entry.video;
      video.controls = false;
      video.removeAttribute('controls');
      window.LlovableMedia.presentation?.(video);
      video.removeAttribute('poster');
      video.defaultMuted = true;
      video.muted = true;
      video.volume = 0;
      video.autoplay = false;
      video.loop = true;
      video.playsInline = true;
      [...video.textTracks].forEach(track => { track.mode = 'disabled'; });
      // Initialize every source promptly without competing full offscreen downloads.
      window.LlovableMedia.hydrate(video, 'metadata');
    }
    pausePreview(entry) {
      entry.video.autoplay = false;
      entry.video.preload = 'metadata';
      if (!entry.video.paused || entry.previewPending) {
        entry.request += 1;
        entry.previewPending = false;
        entry.video.pause();
      }
    }
    preview(entry) {
      if (entry.signal.aborted || !this.mobile.matches) return;
      this.preparePreview(entry);
      if (!this.wantsPreview(entry)) { this.pausePreview(entry); return; }
      const video = entry.video;
      if (!video.paused || entry.previewPending || entry.previewBlocked) return;
      video.preload = 'auto';
      video.autoplay = true;
      entry.previewPending = true;
      const request = ++entry.request;
      try {
        Promise.resolve(video.play()).then(() => {
          if (request === entry.request && !this.wantsPreview(entry)) this.pausePreview(entry);
        }).catch(() => {
          if (request === entry.request) entry.previewBlocked = true;
        }).finally(() => {
          if (request === entry.request) entry.previewPending = false;
        });
      } catch (_) {
        entry.previewPending = false;
        entry.previewBlocked = true;
      }
    }
    setPreviewVisibility(entry, visible) {
      if (visible && !entry.previewVisible) entry.previewBlocked = false;
      entry.previewVisible = visible;
      this.preview(entry);
    }
    measurePreviewVisibility() {
      if (!this.mobile.matches || this.controller.signal.aborted) return;
      const track = this.track.getBoundingClientRect();
      const left = Math.max(0, track.left), right = Math.min(window.innerWidth, track.right);
      const top = Math.max(0, track.top), bottom = Math.min(window.innerHeight, track.bottom);
      this.entries.forEach(entry => {
        const rect = entry.card.getBoundingClientRect();
        this.setPreviewVisibility(entry, left < right && top < bottom && rect.right > left && rect.left < right && rect.bottom > top && rect.top < bottom);
      });
    }
    schedulePreviewMeasure() {
      if (!this.mobile.matches || this.previewMeasureFrame !== null) return;
      this.previewMeasureFrame = requestAnimationFrame(() => {
        this.previewMeasureFrame = null;
        this.measurePreviewVisibility();
      });
    }
    applyMode() {
      if (!this.mobile.matches) this.closeFullscreen();
      this.pauseAll();
      this.previewObserver?.disconnect();
      this.entries.forEach(entry => {
        this.sync(entry);
        if (this.mobile.matches) {
          this.preparePreview(entry);
          this.previewObserver?.observe(entry.card);
        }
        else {
          entry.mobilePrepared = false;
          entry.previewVisible = false;
          entry.previewBlocked = false;
          entry.video.autoplay = false;
          entry.video.loop = false;
          window.LlovableMedia.presentation?.(entry.video);
          if (entry.poster) entry.video.setAttribute('poster', entry.poster);
        }
      });
      if (this.mobile.matches) this.measurePreviewVisibility();
    }
    openFullscreen(entry) {
      this.closeFullscreen();
      const host = document.createElement('dialog');
      host.className = 'product-video-carousel__fullscreen';
      host.setAttribute('aria-label', `Review video ${entry.index + 1}`);
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'product-video-carousel__close';
      close.setAttribute('aria-label', 'Close review video');
      close.textContent = '×';
      const video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.muted = false;
      video.volume = 1;
      video.preload = 'auto';
      window.LlovableMedia.copySources(entry.video, video);
      host.append(video, close);
      document.body.append(host);
      this.fullscreen = {host, video, entry};
      this.entries.forEach(item => this.pausePreview(item));
      close.addEventListener('click', () => this.closeFullscreen());
      host.addEventListener('cancel', event => { event.preventDefault(); this.closeFullscreen(); });
      host.addEventListener('close', () => { if (this.fullscreen?.host === host) this.closeFullscreen(); });
      video.addEventListener('error', () => {
        if (this.fullscreen?.video === video && window.LlovableMedia.fallback(video)) video.play().catch(() => {});
      });
      host.showModal();
      video.addEventListener('webkitendfullscreen', () => { if (this.fullscreen?.video === video) this.closeFullscreen(); });
      const nativeFullscreen = () => {
        if (this.fullscreen?.video !== video || !video.webkitEnterFullscreen) return;
        try { video.webkitEnterFullscreen(); } catch (_) { /* The full-viewport dialog remains available. */ }
      };
      if (video.webkitEnterFullscreen) video.addEventListener('loadedmetadata', nativeFullscreen, {once:true});
      window.LlovableMedia.hydrate(video, 'auto');
      video.play().catch(() => {});
      if (video.readyState >= 1) nativeFullscreen();
      close.focus();
    }
    closeFullscreen() {
      if (!this.fullscreen) return;
      const {host, video, entry} = this.fullscreen;
      this.fullscreen = null;
      video.muted = true;
      try { if (video.webkitDisplayingFullscreen && video.webkitExitFullscreen) video.webkitExitFullscreen(); } catch (_) {}
      window.LlovableMedia.release(video);
      host.close();
      host.remove();
      if (entry.card.isConnected) entry.card.focus({preventScroll: true});
      if (this.mobile.matches) this.measurePreviewVisibility();
    }
    warm(entry) {
      if (entry.signal.aborted) return;
      if (this.mobile.matches) { entry.previewBlocked = false; this.preview(entry); return; }
      window.LlovableMedia.hydrate(entry.video, entry.requested ? 'auto' : 'metadata');
    }
    cancelReveal(entry) {
      if (entry.frameCallback !== null && entry.video.cancelVideoFrameCallback) {
        entry.video.cancelVideoFrameCallback(entry.frameCallback);
      }
      if (entry.revealFrame !== null) cancelAnimationFrame(entry.revealFrame);
      entry.frameCallback = null;
      entry.revealFrame = null;
    }
    revealWhenPainted(entry) {
      if (entry.card.classList.contains('has-started')) return;
      this.cancelReveal(entry);
      const request = entry.request;
      const reveal = () => {
        entry.frameCallback = null;
        entry.revealFrame = null;
        if (!this.isCurrent(entry) || request !== entry.request || entry.video.paused || entry.video.readyState < 2) return;
        entry.card.classList.add('has-started');
      };
      // `playing` may arrive before a decoded frame reaches the screen.
      // Keep the cover in place until the compositor has an actual frame.
      if (entry.video.requestVideoFrameCallback) {
        entry.frameCallback = entry.video.requestVideoFrameCallback(reveal);
      } else {
        entry.revealFrame = requestAnimationFrame(() => {
          entry.revealFrame = requestAnimationFrame(reveal);
        });
      }
    }
    isCurrent(entry) {
      return !entry.signal.aborted && entry.requested && this.active === entry;
    }
    sync(entry) {
      if (this.mobile.matches) {
        entry.card.classList.remove('is-playing');
        entry.card.setAttribute('aria-pressed', 'false');
        entry.card.setAttribute('aria-haspopup', 'dialog');
        entry.card.setAttribute('aria-label', `Open review video ${entry.index + 1} of ${this.entries.length} fullscreen with sound`);
        return;
      }
      entry.card.removeAttribute('aria-haspopup');
      entry.card.classList.toggle('is-playing', entry.requested);
      entry.card.setAttribute('aria-pressed', String(entry.requested));
      const fallback = `${entry.requested ? 'Pause' : 'Play'} review video ${entry.index + 1} of ${this.entries.length}${entry.requested ? '' : ' with sound'}`;
      entry.card.setAttribute('aria-label', window.Llovable?.themeText?.(entry.requested ? 'pause_video_html' : 'play_video_html', fallback, {index:entry.index+1,count:this.entries.length}) ?? fallback);
    }
    stop(entry) {
      this.cancelReveal(entry);
      entry.previewPending = false;
      if (this.mobile.matches) entry.video.autoplay = false;
      entry.request += 1;
      entry.requested = false;
      entry.video.pause();
      if (this.active === entry) this.active = null;
      this.sync(entry);
    }
    pauseAll() {
      this.entries.forEach(entry => this.stop(entry));
    }
    start(entry) {
      this.pauseAll();
      document.dispatchEvent(new CustomEvent('tipelle:review-play', { detail: this }));
      this.active = entry;
      entry.requested = true;
      const request = ++entry.request;
      this.status.textContent = '';
      const video = entry.video;
      this.warm(entry);
      if (video.ended) video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      video.loop = false;
      video.autoplay = false;
      this.sync(entry);
      this.play(entry, request);
    }
    play(entry, request) {
      const video = entry.video;
      try {
        const pending = video.play();
        if (pending) pending.then(() => {
          // A stale play promise must never interrupt a newer request for this same card.
          if (entry.signal.aborted) return;
          if (!this.mobile.matches && !this.isCurrent(entry)) video.pause();
        }).catch(() => this.failed(entry, request));
      } catch (error) { this.failed(entry, request); }
    }
    failed(entry, request) {
      if (entry.signal.aborted || request !== entry.request || !this.isCurrent(entry)) return;
      if (window.LlovableMedia.fallback(entry.video)) {
        this.play(entry, ++entry.request);
        return;
      }
      this.stop(entry);
      const fallback = `Review video ${entry.index + 1} could not play. Press play to try again.`;
      this.status.textContent = window.Llovable?.themeText?.('video_failed_html',fallback,{index:entry.index+1}) ?? fallback;
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.closeFullscreen();
      this.pauseAll();
      this.controller.abort();
      this.resizeObserver.disconnect();
      this.previewObserver?.disconnect();
      if (this.previewMeasureFrame !== null) cancelAnimationFrame(this.previewMeasureFrame);
      if (this.preloadObserver) this.preloadObserver.disconnect();
      this.entries.forEach(({ card, video }) => {
        window.LlovableMedia.release(video);
        card.classList.remove('has-started');
      });
      this.controller = null;
    }
  }
  customElements.define('tipelle-video-carousel', TipelleVideoCarousel);
})();
